import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './ThemeSettings.css'

const DEFAULT_CUSTOM_COLORS = { bg: '#282828', text: '#eeeeed', accent: '#ef7d00' }

export default function ThemeSettings() {
  const { apiFetch, admin, isOwner } = useAuth()

  const [tenants,      setTenants]      = useState([])
  const [tenantId,     setTenantId]     = useState(isOwner ? null : admin?.tenant_id)
  const [theme,        setTheme]        = useState('dark')
  const [customColors, setCustomColors] = useState(DEFAULT_CUSTOM_COLORS)
  const [saving,       setSaving]       = useState(false)
  const [msg,          setMsg]          = useState(null)
  const [loading,      setLoading]      = useState(false)

  // Load tenant list for owners
  useEffect(() => {
    if (!isOwner) return
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setTenants(data)
          setTenantId(data[0].id)
        }
      })
      .catch(() => {})
  }, [isOwner])

  // Load theme config whenever tenantId changes
  useEffect(() => {
    if (!tenantId) return
    setLoading(true)
    const qs = isOwner ? `?tenantId=${tenantId}` : ''
    apiFetch(`/api/admin/theme-settings${qs}`)
      .then(r => r.json())
      .then(cfg => {
        setTheme(cfg.theme || 'dark')
        setCustomColors(cfg.customColors || DEFAULT_CUSTOM_COLORS)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [tenantId])

  const handleThemeChange = (value) => {
    setTheme(value)
    // First time switching to "custom", seed sensible defaults instead of
    // showing the pickers on whatever was previously loaded as null.
    if (value === 'custom' && !customColors) setCustomColors(DEFAULT_CUSTOM_COLORS)
  }

  const setCustomColor = (key, value) =>
    setCustomColors(prev => ({ ...(prev || DEFAULT_CUSTOM_COLORS), [key]: value }))

  async function handleSave() {
    setSaving(true)
    setMsg(null)
    try {
      const qs = isOwner ? `?tenantId=${tenantId}` : ''
      const res = await apiFetch(`/api/admin/theme-settings${qs}`, {
        method: 'PUT',
        body: JSON.stringify({ theme, customColors: theme === 'custom' ? customColors : null }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Unbekannter Fehler')
      setMsg({ type: 'success', text: 'Einstellungen gespeichert.' })
    } catch (err) {
      setMsg({ type: 'error', text: `Fehler beim Speichern: ${err.message}` })
    } finally {
      setSaving(false)
      setTimeout(() => setMsg(null), 4000)
    }
  }

  if (loading && !isOwner) return <div className="loading-state"><span className="spinner" /> Laden…</div>

  return (
    <div>
      <div className="page-header">
        <h1>Darstellung</h1>
        <p>Hell-/Dunkelmodus und Farben der Check-in-Station für diesen Mandanten.</p>
      </div>

      {isOwner && tenants.length > 0 && (
        <div className="card ts-tenant-card">
          <div className="form-group ts-tenant-form-group">
            <label>Mandant</label>
            <select
              className="form-control"
              value={tenantId || ''}
              onChange={e => setTenantId(e.target.value)}
            >
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {msg && (
        <div className={`alert ${msg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {msg.text}
        </div>
      )}

      <div className="card">
        <p className="section-title">Theme</p>
        <div className="form-group ts-theme-form-group">
          <select
            className="form-control ts-theme-select"
            value={theme}
            onChange={e => handleThemeChange(e.target.value)}
          >
            <option value="dark">Dunkel</option>
            <option value="light">Hell</option>
            <option value="custom">Benutzerdefiniert</option>
          </select>
        </div>

        {theme === 'custom' && (
          <div className="ts-custom-colors">
            <label className="ts-color-field">
              <span>Hintergrund</span>
              <input
                type="color"
                value={customColors?.bg || DEFAULT_CUSTOM_COLORS.bg}
                onChange={e => setCustomColor('bg', e.target.value)}
              />
            </label>
            <label className="ts-color-field">
              <span>Text</span>
              <input
                type="color"
                value={customColors?.text || DEFAULT_CUSTOM_COLORS.text}
                onChange={e => setCustomColor('text', e.target.value)}
              />
            </label>
            <label className="ts-color-field">
              <span>Akzent</span>
              <input
                type="color"
                value={customColors?.accent || DEFAULT_CUSTOM_COLORS.accent}
                onChange={e => setCustomColor('accent', e.target.value)}
              />
            </label>
          </div>
        )}
      </div>

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Speichern…' : 'Speichern'}
      </button>
    </div>
  )
}
